import { deDE } from '@mui/x-data-grid/locales';
import * as React from 'react';
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridColumnVisibilityModel,
  GridToolbar,
} from '@mui/x-data-grid';
import { Box, IconButton, Alert, Typography, LinearProgress, Chip } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { i18next } from '@/config/i18n'
import { EditOutlined, DeleteOutlined, CheckCircleOutline, BlockOutlined } from '@mui/icons-material';
import { Vehicles } from '@/config/types/Vehicles';

export const useStyles = makeStyles(() => ({
  VehicleTable: {
    borderBottom: '1px solid #e0e0e0',
  },
  root: {
    width: '100%',
  },
}));

interface Props {
  vehicles: Vehicles[];
  rowCount: number;
  loading: boolean;
  error?: string | null;
  page: number;
  pageSize: number;
  onPageChange: (newPage: number) => void;
  onPageSizeChange: (newSize: number) => void;
  visibleCols: GridColumnVisibilityModel;
  onColumnVisibilityChange: (model: GridColumnVisibilityModel) => void;
  onEdit: (vehicle: Vehicles) => void;
  onDelete?: (id: string) => void;
  onToggleActive: (vehicle: Vehicles, isActive: boolean) => Promise<void>;
}

const VehicleTable: React.FC<Props> = ({
  vehicles,
  rowCount,
  loading,
  error,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  visibleCols,
  onColumnVisibilityChange,
  onEdit,
  onDelete,
  onToggleActive
}) => {
  const classes = useStyles();
  const [colVisModel, setColVisModel] = React.useState<GridColumnVisibilityModel>({});
  const paginationModel: GridPaginationModel = {
    page: page - 1,
    pageSize,
  };

  const columns: GridColDef[] = [
    { field: 'name', headerName: i18next.t('common:vehicleTable.name'), flex: 0.2, minWidth: 140, filterable: true },
    {
      field: 'is_active',
      headerName: i18next.t('common:vehicleTable.status'),
      width: 120,
      sortable: true,
      renderCell: params => {
        const vehicle = params.row as Vehicles;
        const active = Boolean(vehicle.is_active);
        return (
          <Chip
            clickable
            icon={active ? <CheckCircleOutline fontSize="small" /> : <BlockOutlined fontSize="small" />}
            label={i18next.t(active ? 'common:vehicleTable.active' : 'common:vehicleTable.inactive')}
            color={active ? 'success' : 'error'}
            size="small"
            onClick={async e => {
              e.stopPropagation();
              const next = !active;
              params.api.setEditCellValue({ id: params.id, field: params.field, value: next });
              await onToggleActive(vehicle, next);
            }}
          />
        );
      }
    },
    { field: 'vehicle_routing_type', headerName: i18next.t('common:vehicleTable.vehicleRoutingType'), flex: 0.2, minWidth: 140 },
    { field: 'vehicle_type', headerName: i18next.t('common:vehicleTable.vehicleType'), flex: 0.2, minWidth: 140 },
    { field: 'load_class_name', headerName: i18next.t('common:vehicleTable.loadClassName'), minWidth: 140 },
    { field: 'primary_territory_names', headerName: i18next.t('common:vehicleTable.primaryTerritories'), flex: 0.2, minWidth: 140 },
    { field: 'secondary_territory_names', headerName: i18next.t('common:vehicleTable.secondaryTerritories'), minWidth: 150 },
    {
      field: 'actions',
      flex: 0.7,
      headerName: 'Actions',
      minWidth: 100,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => {
        const vehicle = row as Vehicles;
        return (
          <>
            <IconButton size="small" onClick={e => { e.stopPropagation(); onEdit(vehicle); }}>
              <EditOutlined fontSize="small" />
            </IconButton>
            {onDelete && (
              <IconButton size="small" onClick={e => { e.stopPropagation(); onDelete(vehicle.id); }}>
                <DeleteOutlined fontSize="small" />
              </IconButton>
            )}
          </>
        );
      }
    }
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, width: '100%' }}>
      {/* Error & Empty State */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!error && vehicles.length === 0 && !loading && (
        <Typography align="center" sx={{ my: 2 }}>No vehicles found.</Typography>
      )}

      {/* Grid Container */}
      <Box className={classes.root} sx={{ flex: 1, minHeight: 0 }}>
        {loading && (
          <LinearProgress
            sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: theme => theme.zIndex.snackbar }}
            color="secondary"
          />
        )}

        <DataGrid
          rows={vehicles}
          columns={columns}
          getRowId={(row: Vehicles) => row.id}
          pagination
          paginationMode="client"
          paginationModel={paginationModel}
          onPaginationModelChange={(model) => {
            if (model.page !== paginationModel.page) {
              onPageChange(model.page + 1)
            }
            if (model.pageSize !== paginationModel.pageSize) {
              onPageSizeChange(model.pageSize)
            }
          }}
          pageSizeOptions={[10, 20, 50]}
          filterMode="client"
          sortingMode="client"
          disableRowSelectionOnClick
          //checkboxSelection
          columnVisibilityModel={colVisModel}
          onColumnVisibilityModelChange={setColVisModel}
          slots={{ toolbar: GridToolbar }}
          sx={{
            flex: 1,
            minHeight: 0,
            width: '100%',
            '& .MuiDataGrid-columnHeaders': {
              position: 'sticky',
              top: 0,
              zIndex: 2,
              backgroundColor: 'background.paper',
            },
          }}
          localeText={deDE.components.MuiDataGrid.defaultProps!.localeText}
        />
      </Box>
    </Box>
  );
};

export default VehicleTable;
